import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toIntlDateTimeFormat } from '@zedoc/date';
import {
  CalendarIcon,
  ClockIcon,
  LocationMarkerIcon,
  UserIcon,
} from '@heroicons/react/outline';
import { getRecordColor } from '../../../screens/EpisodesOfCare/helpers';
import useForm from '../../../utils/useForm';
import MetaDataItem from './MetaDataItem';
import EpisodeOfCareDetails from './EpisodeOfCareDetails';
import { callMethod } from '../../../common/utilsClient/ddp/actions';
import {
  apiZedocGetPatientProjects,
  apiZedocGetProjectProcedureMilestones,
  apiZedocGetPatientByIdentifier,
  apiZedocGetBookedClinicalEvents,
} from '../../../common/api/zedoc';
import { PROCEDURE_STATUS_BOOKED } from '../../../common/constants/collections/omop/Procedure';

const ProcedureDetails = ({
  procedure,
  visible,
  onCancel,
  isEditable,
  onSave,
  onDelete,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectsOptions, setProjectsOptions] = useState([]);
  const [milestonesOptions, setMilestonesOptions] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [loadingPatient, setLoadingPatient] = useState(false);
  const [patientIdentifier, setPatientIdentifier] = useState(null);
  const [bookedEventsOptions, setBookedEventsOptions] = useState([]);
  const [loadingBookedEvents, setLoadingBookedEvents] = useState(false);
  const previousPatientIdRef = useRef(null);
  const previousProjectIdRef = useRef(null);
  const currentProcedureIdRef = useRef(null);

  const { context = {} } = procedure || {};
  const reviewQueueMetadata = procedure?.reviewQueueMetadata;

  const { form, isValid, handleSubmit, renderField, setValue, setValues } =
    useForm({
      inputs: {
        patientName: {
          testId: 'form-field-patient-name',
          label: t('forms:recipientName.label'),
          type: 'text',
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: true,
          muted: true,
          disabled: true,
          schema: {
            type: String,
            optional: true,
          },
        },
        patientId: {
          testId: 'form-field-patient-id',
          label: t('forms:recipientId.label'),
          type: 'text',
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: !isEditable || !reviewQueueMetadata,
          muted: true,
          disabled: isSubmitting,
          schema: {
            type: String,
          },
        },
        projectId: {
          testId: 'form-field-project-id',
          label: t('forms:projectId.label'),
          type: reviewQueueMetadata && isEditable ? 'react-select' : 'text',
          options: projectsOptions,
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: !isEditable,
          muted: true,
          disabled: isSubmitting || loadingProjects,
          schema: {
            type: String,
            optional: true,
          },
        },
        milestoneId: {
          testId: 'form-field-roles',
          label: t('milestones', { count: 1 }),
          type: reviewQueueMetadata && isEditable ? 'react-select' : 'text',
          options: milestonesOptions,
          // Use empty defaultValue to prevent useForm from resetting on context changes
          // Values are set via setValues in the useEffect below
          defaultValue: '',
          showValueOnly: !isEditable,
          muted: true,
          disabled: isSubmitting || loadingMilestones,
          schema: {
            type: String,
            optional: true,
          },
        },
        linkedEventId: {
          testId: 'form-field-linked-event-id',
          label: t('forms:linkedEventId.label', {
            defaultValue: 'Link to existing booked procedure',
          }),
          type: 'react-select',
          options: bookedEventsOptions,
          defaultValue: '',
          showValueOnly: !isEditable || !reviewQueueMetadata,
          muted: true,
          disabled: isSubmitting || loadingBookedEvents,
          schema: {
            type: String,
            optional: true,
          },
        },
      },
      onSubmit: async (values) => {
        if (onSave) {
          setIsSubmitting(true);
          try {
            await onSave(values);
            onCancel();
          } catch (error) {
            console.error('Error saving procedure:', error);
          } finally {
            setIsSubmitting(false);
          }
        } else {
          onCancel();
        }
      },
    });

  useEffect(() => {
    const patientId = form.patientId;
    const currentProjectId = form.projectId;

    if (!patientId || !isEditable || !reviewQueueMetadata) {
      setProjectsOptions([]);
      return;
    }

    const namespace =
      procedure?.patientIdentifier?.namespace ||
      'default/national_health_identifier';
    const patientIdentifier = {
      namespace: namespace,
      value: patientId,
    };

    setLoadingProjects(true);
    dispatch(callMethod(apiZedocGetPatientProjects, { patientIdentifier }))
      .then((projects) => {
        const projectsList = projects || [];
        setProjectsOptions(projectsList);

        if (
          currentProjectId &&
          projectsList.length > 0 &&
          !projectsList.some((p) => p.value === currentProjectId)
        ) {
          setValue('projectId', '');
          setValue('milestoneId', '');
        } else if (projectsList.length === 0 && currentProjectId) {
          // No projects found for this patient - clear selection
          setValue('projectId', '');
          setValue('milestoneId', '');
        }
      })
      .catch((error) => {
        setProjectsOptions([]);
        setValue('projectId', '');
        setValue('milestoneId', '');
      })
      .finally(() => {
        setLoadingProjects(false);
      });
  }, [form.patientId, isEditable, reviewQueueMetadata, procedure, dispatch]);

  useEffect(() => {
    const projectId = form.projectId;
    const currentMilestoneId = form.milestoneId;

    previousProjectIdRef.current = projectId;

    if (!projectId || !isEditable || !reviewQueueMetadata) {
      setMilestonesOptions([]);
      // Only clear milestoneId in editable mode - in read-only mode we display the label
      return;
    }

    setLoadingMilestones(true);
    dispatch(callMethod(apiZedocGetProjectProcedureMilestones, { projectId }))
      .then((milestones) => {
        const milestonesList = milestones || [];
        setMilestonesOptions(milestonesList);

        if (currentMilestoneId) {
          const milestoneExists = milestonesList.some(
            (m) => m.value === currentMilestoneId,
          );
          console.log('milestoneExists', milestoneExists);
          console.log('currentMilestoneId', currentMilestoneId);
          console.log('milestonesList', milestonesList);
          if (!milestoneExists) {
            setValue('milestoneId', '');
          }
        }
      })
      .catch((error) => {
        setMilestonesOptions([]);
        setValue('milestoneId', '');
      })
      .finally(() => {
        setLoadingMilestones(false);
      });
  }, [form.projectId, isEditable, reviewQueueMetadata, dispatch]);

  useEffect(() => {
    const projectId = form.projectId;
    const patientId = form.patientId;

    if (!projectId || !patientId || !isEditable || !reviewQueueMetadata) {
      setBookedEventsOptions([]);
      return;
    }

    const identifier = {
      namespace:
        procedure?.patientIdentifier?.namespace ||
        'default/national_health_identifier',
      value: patientId,
    };

    setLoadingBookedEvents(true);
    dispatch(
      callMethod(apiZedocGetBookedClinicalEvents, {
        patientIdentifier: identifier,
        projectId,
        clinicalEventsType: 'procedures',
      }),
    )
      .then((events) => {
        setBookedEventsOptions(events || []);
      })
      .catch((error) => {
        console.error('Error fetching booked events:', error);
        setBookedEventsOptions([]);
      })
      .finally(() => {
        setLoadingBookedEvents(false);
      });
  }, [
    form.projectId,
    form.patientId,
    isEditable,
    reviewQueueMetadata,
    dispatch,
    procedure,
  ]);

  useEffect(() => {
    const procedureId = procedure?._id || procedure?.eventId;

    if (procedure) {
      const isNewProcedure = currentProcedureIdRef.current !== procedureId;

      // Always update values when procedure data changes (to handle async data loading)
      const identifier = procedure.patientIdentifier || {
        namespace: 'external/omop_person_id',
        value: procedure.patientId,
      };

      // Always update non-editable fields when procedure data changes (to handle async data loading)
      // But only set patientId when switching to a new procedure (to preserve user edits)
      const updates = {
        patientName: procedure.context?.patientName || '',
        projectId:
          isEditable && reviewQueueMetadata
            ? procedure.context?.projectId || ''
            : procedure.context?.projectLabel ||
              procedure.context?.projectId ||
              '',
        milestoneId:
          isEditable && reviewQueueMetadata
            ? procedure.context?.milestoneId || ''
            : procedure.context?.milestoneLabel ||
              procedure.context?.milestoneId ||
              '',
      };

      // Only set patientId when switching to a new procedure
      if (isNewProcedure) {
        updates.patientId =
          procedure.patientId || procedure.patientIdentifier?.value || '';
      }

      setValues(updates);

      if (isNewProcedure) {
        currentProcedureIdRef.current = procedureId;
        previousPatientIdRef.current = procedure.patientId;
        previousProjectIdRef.current = procedure.context?.projectId || '';

        setPatientIdentifier(identifier);

        setProjectsOptions([]);
        setMilestonesOptions([]);

        if (
          procedure.patientId &&
          isEditable &&
          reviewQueueMetadata &&
          !procedure.context?.patientName
        ) {
          setLoadingPatient(true);
          dispatch(
            callMethod(apiZedocGetPatientByIdentifier, {
              patientIdentifier: identifier,
            }),
          )
            .then((patient) => {
              if (patient && patient.patientName) {
                setValue('patientName', patient.patientName);
              }
            })
            .catch((error) => {
              console.error(
                'Error fetching patient name for new procedure:',
                error,
              );
            })
            .finally(() => {
              setLoadingPatient(false);
            });
        }

        if (procedure.patientId && isEditable && reviewQueueMetadata) {
          setLoadingProjects(true);
          dispatch(
            callMethod(apiZedocGetPatientProjects, {
              patientIdentifier: identifier,
            }),
          )
            .then((projects) => {
              setProjectsOptions(projects || []);
            })
            .catch((error) => {
              console.error(
                'Error fetching projects for new procedure:',
                error,
              );
              setProjectsOptions([]);
            })
            .finally(() => {
              setLoadingProjects(false);
            });
        }

        if (procedure.context?.projectId && isEditable && reviewQueueMetadata) {
          setLoadingMilestones(true);
          dispatch(
            callMethod(apiZedocGetProjectProcedureMilestones, {
              projectId: procedure.context.projectId,
            }),
          )
            .then((milestones) => {
              setMilestonesOptions(milestones || []);
            })
            .catch((error) => {
              console.error(
                'Error fetching milestones for new procedure:',
                error,
              );
              setMilestonesOptions([]);
            })
            .finally(() => {
              setLoadingMilestones(false);
            });
        }
      }
    }
  }, [
    procedure,
    isEditable,
    reviewQueueMetadata,
    dispatch,
    setValue,
    setValues,
  ]);

  const handleSaveAndProcess = async () => {
    if (onSave) {
      setIsSubmitting(true);
      try {
        await onSave(form, true);
        onCancel();
      } catch (error) {
        console.error('Error saving and processing procedure:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onCancel();
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      setIsSubmitting(true);
      try {
        await onDelete();
        onCancel();
      } catch (error) {
        console.error('Error deleting procedure:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onCancel();
    }
  };

  return (
    <EpisodeOfCareDetails
      title="Procedure Details"
      visible={visible}
      onCancel={onCancel}
      onSave={handleSubmit}
      onSaveAndProcess={handleSaveAndProcess}
      onDelete={handleDelete}
      isValid={isValid}
      isEditable={isEditable}
    >
      <div className="space-y-4">
        {reviewQueueMetadata && (
          <div
            className={`p-4 rounded-md ${
              reviewQueueMetadata.severity === 'critical'
                ? 'bg-red-100 dark:bg-red-900'
                : reviewQueueMetadata.severity === 'high'
                ? 'bg-yellow-200 dark:bg-yellow-800'
                : 'bg-gray-100 dark:bg-gray-800'
            }`}
          >
            <div className="font-semibold mb-2">
              {reviewQueueMetadata.severity?.toUpperCase()} -{' '}
              {reviewQueueMetadata.type}
            </div>
            <div className="text-sm">
              {reviewQueueMetadata.reasons?.map((reasonObj, index) => (
                <div key={index}>
                  •{' '}
                  {typeof reasonObj === 'string'
                    ? reasonObj
                    : reasonObj.reason || JSON.stringify(reasonObj)}
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="text-lg font-normal">Patient Information</h2>

        <div className="grid grid-cols-2 gap-4">
          {renderField('patientId')}
          {renderField('patientName')}
          {renderField('projectId')}
          {renderField('milestoneId')}
        </div>

        <div
          className={`mt-4 ${
            procedure?.status !== PROCEDURE_STATUS_BOOKED &&
            reviewQueueMetadata &&
            isEditable &&
            bookedEventsOptions.length > 0
              ? ''
              : 'hidden'
          }`}
        >
          {renderField('linkedEventId')}
          <p className="text-xs text-neutral-500 mt-1">
            {t('forms:linkedEventId.help', {
              defaultValue:
                'Select an existing booked procedure to update its status instead of creating a new one',
            })}
          </p>
        </div>

        <hr className="border-divider" />

        <h2 className="text-lg font-normal">Procedure Details</h2>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            data-testid="procedure-type"
            label="Type"
            content={context.type || '-'}
          />
          <MetaDataItem
            data-testid="procedure-status"
            label="Status"
            content={
              <span
                className={`${getRecordColor(
                  procedure.status,
                )} text-xs px-2 py-1 rounded`}
              >
                {procedure.status || '-'}
              </span>
            }
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            data-testid="procedure-date"
            label="Date"
            content={
              procedure.start
                ? toIntlDateTimeFormat(language, procedure.start)
                : '-'
            }
            icon={<CalendarIcon className="w-4 h-4" />}
          />
          <MetaDataItem
            data-testid="procedure-time"
            label="Time"
            content={
              procedure.start
                ? toIntlDateTimeFormat(language, procedure.start, {
                    timeStyle: 'short',
                  })
                : '-'
            }
            icon={<ClockIcon className="w-4 h-4" />}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <MetaDataItem
            data-testid="procedure-duration"
            label="Duration"
            content={
              procedure.start && procedure.end
                ? `${(procedure.end - procedure.start) / 60000} minutes`
                : '-'
            }
          />
          <MetaDataItem
            data-testid="procedure-location"
            label="Location"
            content={context.location || '-'}
            icon={<LocationMarkerIcon className="w-4 h-4" />}
          />
        </div>

        <hr className="border-divider" />

        <MetaDataItem
          data-testid="procedure-clinician"
          label="Clinician"
          content={context.clinicianName || '-'}
          icon={<UserIcon className="w-4 h-4" />}
        />
        {context.description && (
          <MetaDataItem
            data-testid="procedure-description"
            label="Description"
            content={context.description}
          />
        )}
        <MetaDataItem
          data-testid="procedure-notes"
          label="Notes"
          content={
            <div className="bg-neutral-400 p-2 rounded mt-1">
              {context.notes || '-'}
            </div>
          }
        />

        {context.appointment && (
          <>
            <hr className="border-divider" />
            <h2 className="text-lg font-normal">Linked Appointment</h2>

            <div className="grid grid-cols-2 gap-4">
              <MetaDataItem
                data-testid="procedure-appointment-date"
                label="Appointment Date"
                content={toIntlDateTimeFormat(
                  language,
                  context.appointment.appointmentStart,
                )}
                icon={<CalendarIcon className="w-4 h-4" />}
              />
              <MetaDataItem
                data-testid="procedure-appointment-time"
                label="Appointment Time"
                content={`${toIntlDateTimeFormat(
                  language,
                  context.appointment.appointmentStart,
                  {
                    timeStyle: 'short',
                  },
                )} - ${toIntlDateTimeFormat(
                  language,
                  context.appointment.appointmentEnd,
                  {
                    timeStyle: 'short',
                  },
                )}`}
                icon={<ClockIcon className="w-4 h-4" />}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <MetaDataItem
                data-testid="procedure-appointment-type"
                label="Appointment Type"
                content={context.appointment.appointmentType || 'N/A'}
              />
              <MetaDataItem
                data-testid="procedure-appointment-status"
                label="Appointment Status"
                content={
                  <span
                    className={`${getRecordColor(
                      context.appointment.appointmentStatus,
                    )} text-xs px-2 py-1 rounded`}
                  >
                    {context.appointment.appointmentStatus}
                  </span>
                }
              />
            </div>

            <MetaDataItem
              data-testid="procedure-appointment-id"
              label="Appointment ID"
              content={context.appointment.source_visit_occurrence_id}
            />
          </>
        )}

        <hr className="border-divider" />

        <MetaDataItem
          data-testid="procedure-id"
          label="Procedure ID"
          content={procedure.eventId}
        />
        <MetaDataItem
          data-testid="procedure-event-type"
          label="Record Type"
          content="Procedure"
        />
      </div>
    </EpisodeOfCareDetails>
  );
};

ProcedureDetails.propTypes = {
  procedure: PropTypes.shape({
    patientId: PropTypes.string,
    eventId: PropTypes.string,
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
    status: PropTypes.string,
    context: PropTypes.shape({
      patientName: PropTypes.string,
      projectId: PropTypes.string,
      description: PropTypes.string,
      location: PropTypes.string,
    }),
  }),
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  isEditable: PropTypes.bool,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
};

ProcedureDetails.defaultProps = {
  procedure: {},
  visible: false,
  onCancel: () => {},
  isEditable: false,
  onSave: null,
  onDelete: null,
};

export default ProcedureDetails;
